import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { useAuthStore } from '@/stores/auth'
import { ROUTES } from '@/constants/routes'
import { invalidateFindHelpCache } from '@/lib/findHelpData'
import { CATEGORY_OPTIONS } from '@/components/findhelp/ResourceGuides'
import {
  fetchResourceSubmissions,
  reviewResourceSubmission,
  updateResourceSubmission,
  deleteResourceSubmission,
  type ResourceSubmission,
  type ResourceSubmissionStatus,
} from '@/lib/resourceSubmissions'
import {
  adminGetJobs, adminCreateJob, adminUpdateJob, adminDeleteJob,
  adminGetProviders, adminCreateProvider, adminUpdateProvider, adminDeleteProvider,
  adminGetGovPrograms, adminCreateGovProgram, adminUpdateGovProgram, adminDeleteGovProgram,
  adminGetNgoPrograms, adminCreateNgoProgram, adminUpdateNgoProgram, adminDeleteNgoProgram,
} from '@/lib/adminResources'
import type { JobListing, Provider, GovProgram, NgoProgram } from '@/lib/accesshub'

type MainTab = 'submissions' | 'jobs' | 'care' | 'aid'
type AidSubTab = 'gov' | 'ngo'

const JOB_TYPES = ['Full-time', 'Part-time', 'Contract', 'Freelance', 'Volunteer', 'Internship']
const PAY_PERIODS = ['/mo', '/hr', '/day', '/project']
const PROVIDER_CATEGORIES = ['Therapy Center', 'Hospital', 'Education', 'Support Organization']
const PH_REGIONS = [
  'NCR — Metro Manila', 'CAR — Cordillera', 'Region I — Ilocos', 'Region II — Cagayan Valley',
  'Region III — Central Luzon', 'Region IV-A — CALABARZON', 'Region IV-B — MIMAROPA',
  'Region V — Bicol', 'Region VI — Western Visayas', 'Region VII — Central Visayas',
  'Region VIII — Eastern Visayas', 'Region IX — Zamboanga Peninsula', 'Region X — Northern Mindanao',
  'Region XI — Davao', 'Region XII — SOCCSKSARGEN', 'Region XIII — Caraga', 'BARMM — Bangsamoro',
]

const inputCls = 'w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-beacon-400 placeholder:text-slate-400'
const checkCls = 'flex items-center gap-2.5 rounded-xl border border-slate-200 px-4 py-3 cursor-pointer hover:bg-slate-50'

const JOB_TAG_PRESETS = ['Remote', 'Flexible Hours', 'Training Provided', 'PWD-Friendly', 'Entry Level', 'Immediate Hire']
const ACCESSIBILITY_TAG_PRESETS = ['Wheelchair Ramp', 'Elevator', 'Accessible Restroom', 'Braille Signage', 'Sign Language Staff', 'Quiet Room', 'Service Animal Friendly', 'PWD Parking']
const AID_TAG_PRESETS = ['Cash Assistance', 'Medical', 'Education', 'Housing', 'Food', 'PWD-Specific', 'Mental Health', 'Legal Aid', 'Transportation', 'Emergency Relief']

export function TagChipSelector({ value, onChange, presets, placeholder }: { value: string; onChange: (v: string) => void; presets: string[]; placeholder: string }) {
  const [custom, setCustom] = useState('')
  const selected = value.split(',').map(s => s.trim()).filter(Boolean)

  function toggle(tag: string) {
    const next = selected.includes(tag) ? selected.filter(t => t !== tag) : [...selected, tag]
    onChange(next.join(', '))
  }

  function addCustom() {
    const trimmed = custom.trim()
    if (!trimmed || selected.includes(trimmed)) { setCustom(''); return }
    onChange([...selected, trimmed].join(', '))
    setCustom('')
  }

  const customTags = selected.filter(t => !presets.includes(t))

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {presets.map(tag => (
          <button key={tag} type="button" onClick={() => toggle(tag)}
            className={`text-xs font-medium px-2.5 py-1 rounded-full border transition-colors ${selected.includes(tag) ? 'bg-beacon-600 text-white border-beacon-600' : 'bg-white text-slate-600 border-slate-300 hover:border-beacon-400 hover:text-beacon-600'}`}>
            {tag}
          </button>
        ))}
        {customTags.map(tag => (
          <button key={tag} type="button" onClick={() => toggle(tag)}
            className="text-xs font-medium px-2.5 py-1 rounded-full border bg-slate-100 text-slate-700 border-slate-300 hover:bg-red-50 hover:border-red-300 hover:text-red-600 transition-colors">
            {tag} ×
          </button>
        ))}
      </div>
      <div className="flex gap-2">
        <input value={custom} onChange={e => setCustom(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addCustom() } }}
          placeholder={placeholder} className={`${inputCls} flex-1`} />
        {custom.trim() && (
          <button type="button" onClick={addCustom} className="px-3 py-2 rounded-lg bg-beacon-600 text-white text-xs font-semibold hover:bg-beacon-700 flex-shrink-0">Add</button>
        )}
      </div>
    </div>
  )
}

// ─── Job form ────────────────────────────────────────────────────────────────

export type JobDraft = {
  icon: string; title: string; company: string; source: string; location: string
  jobType: string; hours: string; pay: string; payPeriod: string
  description: string; requirements: string; tags: string; applyUrl: string
  ra7277Compliant: boolean; status: 'active' | 'delisted' | 'closed'
}

export const emptyJob = (): JobDraft => ({
  icon: '', title: '', company: '', source: '', location: '',
  jobType: '', hours: '', pay: '', payPeriod: '',
  description: '', requirements: '', tags: '', applyUrl: '',
  ra7277Compliant: false, status: 'active',
})

export function jobDraftToPayload(d: JobDraft) {
  return {
    title: d.title.trim(),
    company: d.company.trim(),
    source: d.source.trim(),
    location: d.location.trim(),
    jobType: d.jobType || undefined,
    hours: d.hours.trim() || undefined,
    pay: d.pay.trim() ? `${d.pay.trim()}${d.payPeriod}` : undefined,
    description: d.description.trim(),
    requirements: d.requirements.split('\n').map(s => s.trim()).filter(Boolean),
    tags: d.tags.split(',').map(s => s.trim()).filter(Boolean),
    applyUrl: d.applyUrl.trim() || undefined,
    ra7277Compliant: d.ra7277Compliant,
    employerId: 'admin',
    status: d.status,
  }
}

export function jobToEdit(j: JobListing & { source?: string }): JobDraft {
  const payRaw = j.pay ?? ''
  const period = PAY_PERIODS.find(p => payRaw.endsWith(p)) ?? ''
  const payNum = period ? payRaw.slice(0, -period.length) : payRaw
  return {
    icon: '',
    title: j.title ?? '',
    company: j.company ?? '',
    source: j.source ?? '',
    location: j.location ?? '',
    jobType: j.jobType ?? '',
    hours: j.hours ?? '',
    pay: payNum,
    payPeriod: period,
    description: j.description ?? '',
    requirements: (j.requirements ?? []).join('\n'),
    tags: (j.tags ?? []).join(', '),
    applyUrl: j.applyUrl ?? '',
    ra7277Compliant: j.ra7277Compliant ?? false,
    status: j.status ?? 'active',
  }
}

export function JobForm({ draft, onChange }: { draft: JobDraft; onChange: (d: JobDraft) => void }) {
  const set = (k: keyof JobDraft, v: string | boolean) => onChange({ ...draft, [k]: v })
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-[2.5rem_1fr] gap-2">
        <input value={draft.icon} onChange={e => set('icon', e.target.value)} maxLength={2} placeholder="💼" className={`${inputCls} text-center text-lg`} />
        <input value={draft.title} onChange={e => set('title', e.target.value)} placeholder="Job title *" className={inputCls} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <input value={draft.company} onChange={e => set('company', e.target.value)} placeholder="Company name *" className={inputCls} />
        <input value={draft.source} onChange={e => set('source', e.target.value)} placeholder="Source / referral (optional)" className={inputCls} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <input value={draft.location} onChange={e => set('location', e.target.value)} placeholder="City" className={inputCls} />
        <select value={draft.status} onChange={e => set('status', e.target.value)} className={`${inputCls} text-slate-700`}>
          <option value="active">Active</option>
          <option value="delisted">Delisted</option>
          <option value="closed">Closed</option>
        </select>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <select value={draft.jobType} onChange={e => set('jobType', e.target.value)} className={`${inputCls} text-slate-700`}>
          <option value="">— Job type —</option>
          {JOB_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <input value={draft.hours} onChange={e => set('hours', e.target.value)} placeholder="Hours (e.g. 40hrs/wk)" className={inputCls} />
      </div>
      <div className="flex gap-2">
        <input value={draft.pay} onChange={e => set('pay', e.target.value)} placeholder="Rate (e.g. ₱25,000)" className={`${inputCls} flex-1`} />
        <select value={draft.payPeriod} onChange={e => set('payPeriod', e.target.value)} className="rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-beacon-400 flex-shrink-0">
          <option value="">— rate —</option>
          {PAY_PERIODS.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>
      <textarea value={draft.description} onChange={e => set('description', e.target.value)} rows={3} placeholder="Job description *" className={`${inputCls} resize-none`} />
      <textarea value={draft.requirements} onChange={e => set('requirements', e.target.value)} rows={3} placeholder="Requirements — one per line" className={`${inputCls} resize-none`} />
      <input value={draft.applyUrl} onChange={e => set('applyUrl', e.target.value)} placeholder="Apply link or website (optional)" className={inputCls} />
      <TagChipSelector value={draft.tags} onChange={v => set('tags', v)} presets={JOB_TAG_PRESETS} placeholder="Custom tag (press Enter to add)" />
      <label className={checkCls}>
        <input type="checkbox" checked={draft.ra7277Compliant} onChange={e => set('ra7277Compliant', e.target.checked)} className="accent-beacon-600 w-4 h-4 flex-shrink-0" />
        <div>
          <p className="text-sm font-semibold text-slate-800">⚖️ RA 7277 Compliant</p>
          <p className="text-xs text-slate-400">Employer actively hires persons with disability</p>
        </div>
      </label>
    </div>
  )
}

// ─── Provider form ────────────────────────────────────────────────────────────

export type ProviderDraft = {
  name: string; category: string; address: string; phone: string; email: string
  website: string; rate: string; description: string; accessibilityTags: string
  pwdDiscount: boolean; ra7277Compliant: boolean; beaconQrEnabled: boolean
  status: 'active' | 'delisted'
}

export const emptyProvider = (): ProviderDraft => ({
  name: '', category: '', address: '', phone: '', email: '',
  website: '', rate: '', description: '', accessibilityTags: '',
  pwdDiscount: false, ra7277Compliant: false, beaconQrEnabled: false, status: 'active',
})

export function providerDraftToPayload(d: ProviderDraft) {
  return {
    name: d.name.trim(),
    category: d.category,
    address: d.address.trim(),
    phone: d.phone.trim() || undefined,
    email: d.email.trim() || undefined,
    website: d.website.trim() || undefined,
    rate: d.rate.trim() || undefined,
    description: d.description.trim(),
    accessibilityTags: d.accessibilityTags.split(',').map(s => s.trim()).filter(Boolean),
    pwdDiscount: d.pwdDiscount,
    ra7277Compliant: d.ra7277Compliant,
    beaconQrEnabled: d.beaconQrEnabled,
    ownerId: 'admin',
    status: d.status,
  }
}

export function providerToEdit(p: Provider): ProviderDraft {
  return {
    name: p.name ?? '', category: p.category ?? '', address: p.address ?? '',
    phone: p.phone ?? '', email: p.email ?? '', website: p.website ?? '', rate: p.rate ?? '',
    description: p.description ?? '',
    accessibilityTags: (p.accessibilityTags ?? []).join(', '),
    pwdDiscount: p.pwdDiscount ?? false, ra7277Compliant: p.ra7277Compliant ?? false,
    beaconQrEnabled: p.beaconQrEnabled ?? false, status: p.status ?? 'active',
  }
}

export function ProviderForm({ draft, onChange }: { draft: ProviderDraft; onChange: (d: ProviderDraft) => void }) {
  const set = (k: keyof ProviderDraft, v: string | boolean) => onChange({ ...draft, [k]: v })
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <input value={draft.name} onChange={e => set('name', e.target.value)} placeholder="Provider name *" className={inputCls} />
        <select value={draft.category} onChange={e => set('category', e.target.value)} className={`${inputCls} text-slate-700`}>
          <option value="">— Category *</option>
          {PROVIDER_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <input value={draft.address} onChange={e => set('address', e.target.value)} placeholder="Address *" className={inputCls} />
      <div className="grid grid-cols-2 gap-2">
        <input value={draft.phone} onChange={e => set('phone', e.target.value)} placeholder="Phone (optional)" className={inputCls} />
        <input value={draft.email} onChange={e => set('email', e.target.value)} placeholder="Email (optional)" className={inputCls} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <input value={draft.website} onChange={e => set('website', e.target.value)} placeholder="Website (optional)" className={inputCls} />
        <input value={draft.rate} onChange={e => set('rate', e.target.value)} placeholder="Rate (e.g. ₱600/session)" className={inputCls} />
      </div>
      <select value={draft.status} onChange={e => set('status', e.target.value)} className={`${inputCls} text-slate-700`}>
        <option value="active">Active</option>
        <option value="delisted">Delisted</option>
      </select>
      <textarea value={draft.description} onChange={e => set('description', e.target.value)} rows={3} placeholder="Description *" className={`${inputCls} resize-none`} />
      <TagChipSelector value={draft.accessibilityTags} onChange={v => set('accessibilityTags', v)} presets={ACCESSIBILITY_TAG_PRESETS} placeholder="Custom accessibility tag (press Enter to add)" />
      <div className="grid grid-cols-3 gap-2">
        <label className={checkCls}>
          <input type="checkbox" checked={draft.ra7277Compliant} onChange={e => set('ra7277Compliant', e.target.checked)} className="accent-beacon-600 w-4 h-4 flex-shrink-0" />
          <div><p className="text-xs font-semibold text-slate-800">⚖️ RA 7277</p><p className="text-[10px] text-slate-400">PWD-accessible</p></div>
        </label>
        <label className={checkCls}>
          <input type="checkbox" checked={draft.pwdDiscount} onChange={e => set('pwdDiscount', e.target.checked)} className="accent-beacon-600 w-4 h-4 flex-shrink-0" />
          <div><p className="text-xs font-semibold text-slate-800">💙 PWD Discount</p><p className="text-[10px] text-slate-400">Discount offered</p></div>
        </label>
        <label className={checkCls}>
          <input type="checkbox" checked={draft.beaconQrEnabled} onChange={e => set('beaconQrEnabled', e.target.checked)} className="accent-beacon-600 w-4 h-4 flex-shrink-0" />
          <div><p className="text-xs font-semibold text-slate-800">⚡ Beacon QR</p><p className="text-[10px] text-slate-400">QR check-in enabled</p></div>
        </label>
      </div>
    </div>
  )
}

// ─── Gov program form ─────────────────────────────────────────────────────────

export type GovDraft = { icon: string; name: string; amount: string; description: string; phone: string; region: string; tags: string }
export const emptyGov = (): GovDraft => ({ icon: '', name: '', amount: '', description: '', phone: '', region: '', tags: '' })
export function govToEdit(p: GovProgram): GovDraft {
  return { icon: p.icon ?? '', name: p.name ?? '', amount: p.amount ?? '', description: p.description ?? '', phone: p.phone ?? '', region: p.region ?? '', tags: (p.tags ?? []).join(', ') }
}

export function GovForm({ draft, onChange }: { draft: GovDraft; onChange: (d: GovDraft) => void }) {
  const set = (k: keyof GovDraft, v: string) => onChange({ ...draft, [k]: v })
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-[2.5rem_1fr] gap-2">
        <input value={draft.icon} onChange={e => set('icon', e.target.value)} maxLength={2} placeholder="🏛️" className={`${inputCls} text-center text-lg`} />
        <input value={draft.name} onChange={e => set('name', e.target.value)} placeholder="Program name *" className={inputCls} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <input value={draft.amount} onChange={e => set('amount', e.target.value)} placeholder="Aid amount (e.g. Up to ₱5,000)" className={inputCls} />
        <input value={draft.phone} onChange={e => set('phone', e.target.value)} placeholder="Contact / hotline" className={inputCls} />
      </div>
      <select value={draft.region} onChange={e => set('region', e.target.value)} className={`${inputCls} text-slate-700`}>
        <option value="">— Region (optional) —</option>
        <option value="Nationwide">🇵🇭 Nationwide</option>
        {PH_REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
      </select>
      <textarea value={draft.description} onChange={e => set('description', e.target.value)} rows={3} placeholder="Description *" className={`${inputCls} resize-none`} />
      <TagChipSelector value={draft.tags} onChange={v => set('tags', v)} presets={AID_TAG_PRESETS} placeholder="Custom tag (press Enter to add)" />
    </div>
  )
}

// ─── NGO program form ─────────────────────────────────────────────────────────

export type NgoDraft = { icon: string; name: string; amount: string; description: string; contact: string; website: string; tags: string }
export const emptyNgo = (): NgoDraft => ({ icon: '', name: '', amount: '', description: '', contact: '', website: '', tags: '' })
export function ngoToEdit(p: NgoProgram): NgoDraft {
  return { icon: p.icon ?? '', name: p.name ?? '', amount: p.amount ?? '', description: p.description ?? '', contact: p.contact ?? '', website: p.website ?? '', tags: (p.tags ?? []).join(', ') }
}

export function NgoForm({ draft, onChange }: { draft: NgoDraft; onChange: (d: NgoDraft) => void }) {
  const set = (k: keyof NgoDraft, v: string) => onChange({ ...draft, [k]: v })
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-[2.5rem_1fr] gap-2">
        <input value={draft.icon} onChange={e => set('icon', e.target.value)} maxLength={2} placeholder="🌿" className={`${inputCls} text-center text-lg`} />
        <input value={draft.name} onChange={e => set('name', e.target.value)} placeholder="Organisation name *" className={inputCls} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <input value={draft.amount} onChange={e => set('amount', e.target.value)} placeholder="Aid amount (e.g. Up to ₱10,000)" className={inputCls} />
        <input value={draft.contact} onChange={e => set('contact', e.target.value)} placeholder="Contact / email *" className={inputCls} />
      </div>
      <input value={draft.website} onChange={e => set('website', e.target.value)} placeholder="Website (optional)" className={inputCls} />
      <textarea value={draft.description} onChange={e => set('description', e.target.value)} rows={3} placeholder="Description *" className={`${inputCls} resize-none`} />
      <TagChipSelector value={draft.tags} onChange={v => set('tags', v)} presets={AID_TAG_PRESETS} placeholder="Custom tag (press Enter to add)" />
    </div>
  )
}

// ─── Generic list section ─────────────────────────────────────────────────────

function ListSection<T extends { id: string }>({
  label, items, loading, renderItem, form, formValid, onAdd, onSave, onDelete,
  editingId, setEditingId, addingNew, setAddingNew, saving, deleteConfirmId, setDeleteConfirmId, deleting,
}: {
  label: string; items: T[]; loading: boolean
  renderItem: (item: T) => React.ReactNode
  form: React.ReactNode; formValid: boolean
  onAdd: () => void; onSave: (id: string) => void; onDelete: (id: string) => void
  editingId: string | null; setEditingId: (id: string | null) => void
  addingNew: boolean; setAddingNew: (v: boolean) => void
  saving: boolean; deleteConfirmId: string | null; setDeleteConfirmId: (id: string | null) => void
  deleting: boolean
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">{items.length} {label}</p>
        <button
          onClick={() => { setAddingNew(true); setEditingId(null) }}
          className="flex items-center gap-1.5 text-xs font-semibold text-beacon-700 bg-white border border-beacon-200 hover:bg-beacon-50 rounded-full px-3 py-1.5 transition-colors"
        >
          <i className="fa-solid fa-plus text-[10px]" /> Add New
        </button>
      </div>

      {addingNew && (
        <div className="rounded-xl border border-beacon-200 bg-white p-4 space-y-3 shadow-sm">
          <p className="text-xs font-bold uppercase tracking-wider text-beacon-600">New {label.replace(/s$/, '')}</p>
          {form}
          <div className="flex gap-2 pt-1">
            <button onClick={() => setAddingNew(false)} className="flex-1 py-2 rounded-xl border border-slate-200 text-xs font-semibold text-slate-500 hover:bg-slate-50">Cancel</button>
            <button onClick={onAdd} disabled={!formValid || saving} className="flex-1 py-2 rounded-lg bg-beacon-600 text-xs font-semibold text-white hover:bg-beacon-700 disabled:opacity-50">{saving ? 'Saving…' : 'Add'}</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-14 rounded-xl bg-slate-100 animate-pulse" />)}</div>
      ) : items.length === 0 && !addingNew ? (
        <div className="rounded-xl border-2 border-dashed border-slate-200 p-6 text-center">
          <p className="text-sm text-slate-400">No {label.toLowerCase()} yet. Add one above.</p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {items.map(item => (
            <div key={item.id} className="bg-white rounded-xl border border-slate-200 px-3 py-2.5 space-y-2">
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">{renderItem(item)}</div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => { setEditingId(editingId === item.id ? null : item.id); setAddingNew(false) }}
                    className="w-7 h-7 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                  >
                    <i className={`fa-solid ${editingId === item.id ? 'fa-xmark' : 'fa-pen'} text-[10px]`} />
                  </button>
                  <button
                    onClick={() => setDeleteConfirmId(deleteConfirmId === item.id ? null : item.id)}
                    className="w-7 h-7 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-400 hover:text-danger-600 hover:bg-danger-50 hover:border-danger-200 transition-colors"
                  >
                    <i className="fa-solid fa-trash text-[10px]" />
                  </button>
                </div>
              </div>
              {deleteConfirmId === item.id && (
                <div className="flex items-center gap-2 rounded-lg bg-danger-50 border border-danger-200 px-3 py-2">
                  <p className="text-xs font-semibold text-danger-700 flex-1">Delete permanently?</p>
                  <button onClick={() => setDeleteConfirmId(null)} className="text-xs font-semibold text-slate-500 px-2 py-1 rounded-lg hover:bg-white">Cancel</button>
                  <button onClick={() => onDelete(item.id)} disabled={deleting} className="text-xs font-semibold text-white bg-danger-500 hover:bg-danger-600 disabled:opacity-50 px-3 py-1 rounded-lg">{deleting ? '…' : 'Delete'}</button>
                </div>
              )}
              {editingId === item.id && (
                <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 space-y-3">
                  {form}
                  <div className="flex gap-2">
                    <button onClick={() => setEditingId(null)} className="flex-1 py-2 rounded-xl border border-slate-200 text-xs font-semibold text-slate-500 hover:bg-slate-50">Cancel</button>
                    <button onClick={() => onSave(item.id)} disabled={!formValid || saving} className="flex-1 py-2 rounded-lg bg-beacon-600 text-xs font-semibold text-white hover:bg-beacon-700 disabled:opacity-50">{saving ? 'Saving…' : 'Save'}</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Main view ────────────────────────────────────────────────────────────────

export default function CommandCenterResourcesView() {
  const navigate = useNavigate()
  const isSuperAdmin = useAuthStore((s) => s.isSuperAdmin())
  const commandCenterMfaReady = useAuthStore((s) => s.userDoc?.adminSecurity?.commandCenterMfaReady === true)
  const canUseCommandCenter = isSuperAdmin && (commandCenterMfaReady || import.meta.env.DEV)

  const [mainTab, setMainTab] = useState<MainTab>('submissions')
  const [aidSubTab, setAidSubTab] = useState<AidSubTab>('gov')

  // ── Submissions state ──
  const [resourceSubmissions, setResourceSubmissions] = useState<ResourceSubmission[]>([])
  const [reviewingSubmissionId, setReviewingSubmissionId] = useState<string | null>(null)
  const [resourceFilter, setResourceFilter] = useState<ResourceSubmissionStatus | 'all'>('pending')
  const [expandedSubmissionId, setExpandedSubmissionId] = useState<string | null>(null)
  const [reviewNotes, setReviewNotes] = useState<Record<string, string>>({})
  const [editingSubmissionId, setEditingSubmissionId] = useState<string | null>(null)
  const [editFields, setEditFields] = useState({ title: '', category: '', link: '', description: '', reviewNote: '' })
  const [savingSubmission, setSavingSubmission] = useState(false)
  const [deleteSubmissionConfirmId, setDeleteSubmissionConfirmId] = useState<string | null>(null)
  const [deletingSubmissionId, setDeletingSubmissionId] = useState<string | null>(null)

  // ── Jobs state ──
  const [jobs, setJobs] = useState<(JobListing & { source?: string })[]>([])
  const [jobsLoading, setJobsLoading] = useState(false)
  const [jobDraft, setJobDraft] = useState<JobDraft>(emptyJob())
  const [addingJob, setAddingJob] = useState(false)
  const [editingJobId, setEditingJobId] = useState<string | null>(null)
  const [savingJob, setSavingJob] = useState(false)
  const [deleteJobConfirmId, setDeleteJobConfirmId] = useState<string | null>(null)
  const [deletingJob, setDeletingJob] = useState(false)

  // ── Care state ──
  const [providers, setProviders] = useState<Provider[]>([])
  const [providersLoading, setProvidersLoading] = useState(false)
  const [providerDraft, setProviderDraft] = useState<ProviderDraft>(emptyProvider())
  const [addingProvider, setAddingProvider] = useState(false)
  const [editingProviderId, setEditingProviderId] = useState<string | null>(null)
  const [savingProvider, setSavingProvider] = useState(false)
  const [deleteProviderConfirmId, setDeleteProviderConfirmId] = useState<string | null>(null)
  const [deletingProvider, setDeletingProvider] = useState(false)

  // ── Aid state ──
  const [govPrograms, setGovPrograms] = useState<GovProgram[]>([])
  const [ngoPrograms, setNgoPrograms] = useState<NgoProgram[]>([])
  const [aidLoading, setAidLoading] = useState(false)
  const [govDraft, setGovDraft] = useState<GovDraft>(emptyGov())
  const [ngoDraft, setNgoDraft] = useState<NgoDraft>(emptyNgo())
  const [addingGov, setAddingGov] = useState(false)
  const [addingNgo, setAddingNgo] = useState(false)
  const [editingGovId, setEditingGovId] = useState<string | null>(null)
  const [editingNgoId, setEditingNgoId] = useState<string | null>(null)
  const [savingGov, setSavingGov] = useState(false)
  const [savingNgo, setSavingNgo] = useState(false)
  const [deleteGovConfirmId, setDeleteGovConfirmId] = useState<string | null>(null)
  const [deleteNgoConfirmId, setDeleteNgoConfirmId] = useState<string | null>(null)
  const [deletingGov, setDeletingGov] = useState(false)
  const [deletingNgo, setDeletingNgo] = useState(false)

  useEffect(() => {
    if (!canUseCommandCenter) return
    void loadResourceSubmissions()
  }, [canUseCommandCenter])

  useEffect(() => {
    if (!canUseCommandCenter || mainTab !== 'jobs') return
    setJobsLoading(true)
    adminGetJobs().then(setJobs).catch(console.warn).finally(() => setJobsLoading(false))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainTab])

  useEffect(() => {
    if (!canUseCommandCenter || mainTab !== 'care') return
    setProvidersLoading(true)
    adminGetProviders().then(setProviders).catch(console.warn).finally(() => setProvidersLoading(false))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainTab])

  useEffect(() => {
    if (!canUseCommandCenter || mainTab !== 'aid') return
    setAidLoading(true)
    Promise.all([adminGetGovPrograms(), adminGetNgoPrograms()])
      .then(([gov, ngo]) => { setGovPrograms(gov); setNgoPrograms(ngo) })
      .catch(console.warn)
      .finally(() => setAidLoading(false))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainTab])

  async function loadResourceSubmissions() {
    try { setResourceSubmissions(await fetchResourceSubmissions()) }
    catch (err) { console.warn('[CommandCenterResources] load failed:', err) }
  }

  async function reviewSubmission(id: string, status: 'approved' | 'rejected') {
    setReviewingSubmissionId(id)
    try {
      await reviewResourceSubmission(id, status, reviewNotes[id] ?? '')
      setResourceSubmissions(prev => prev.map(s => s.id === id ? { ...s, status, reviewNote: reviewNotes[id] ?? '' } : s))
      invalidateFindHelpCache()
      setExpandedSubmissionId(id)
    } catch (err) { console.warn('[CommandCenterResources] review failed:', err) }
    finally { setReviewingSubmissionId(null) }
  }

  function startEditSubmission(s: ResourceSubmission) {
    setEditFields({ title: s.title, category: s.category ?? '', link: s.link ?? '', description: s.description, reviewNote: s.reviewNote ?? '' })
    setEditingSubmissionId(s.id)
    setExpandedSubmissionId(s.id)
  }

  async function saveSubmission(id: string) {
    setSavingSubmission(true)
    try {
      await updateResourceSubmission(id, editFields)
      setResourceSubmissions(prev => prev.map(s => s.id === id ? { ...s, ...editFields } : s))
      invalidateFindHelpCache()
      setEditingSubmissionId(null)
    } catch (err) { console.warn('[CommandCenterResources] edit failed:', err) }
    finally { setSavingSubmission(false) }
  }

  async function deleteSubmission(id: string) {
    setDeletingSubmissionId(id)
    try {
      await deleteResourceSubmission(id)
      setResourceSubmissions(prev => prev.filter(s => s.id !== id))
      invalidateFindHelpCache()
      setDeleteSubmissionConfirmId(null)
    } catch (err) { console.warn('[CommandCenterResources] delete failed:', err) }
    finally { setDeletingSubmissionId(null) }
  }

  // Jobs CRUD
  async function addJob() {
    setSavingJob(true)
    try {
      const id = await adminCreateJob(jobDraftToPayload(jobDraft) as Parameters<typeof adminCreateJob>[0])
      setJobs(prev => [{ id, ...jobDraftToPayload(jobDraft), createdAt: Date.now(), updatedAt: Date.now() } as JobListing & { source?: string }, ...prev])
      setJobDraft(emptyJob()); setAddingJob(false); invalidateFindHelpCache()
    } catch (err) { console.warn('[CC Resources] add job failed:', err) }
    finally { setSavingJob(false) }
  }

  async function saveJob(id: string) {
    setSavingJob(true)
    try {
      await adminUpdateJob(id, jobDraftToPayload(jobDraft))
      setJobs(prev => prev.map(j => j.id === id ? { ...j, ...jobDraftToPayload(jobDraft) } : j))
      setEditingJobId(null); setJobDraft(emptyJob()); invalidateFindHelpCache()
    } catch (err) { console.warn('[CC Resources] save job failed:', err) }
    finally { setSavingJob(false) }
  }

  async function deleteJob(id: string) {
    setDeletingJob(true)
    try {
      await adminDeleteJob(id)
      setJobs(prev => prev.filter(j => j.id !== id))
      setDeleteJobConfirmId(null); invalidateFindHelpCache()
    } catch (err) { console.warn('[CC Resources] delete job failed:', err) }
    finally { setDeletingJob(false) }
  }

  // Providers CRUD
  async function addProvider() {
    setSavingProvider(true)
    try {
      const payload = providerDraftToPayload(providerDraft)
      const id = await adminCreateProvider(payload as Parameters<typeof adminCreateProvider>[0])
      setProviders(prev => [...prev, { id, ...payload, createdAt: Date.now(), updatedAt: Date.now() } as Provider].sort((a, b) => a.name.localeCompare(b.name)))
      setProviderDraft(emptyProvider()); setAddingProvider(false); invalidateFindHelpCache()
    } catch (err) { console.warn('[CC Resources] add provider failed:', err) }
    finally { setSavingProvider(false) }
  }

  async function saveProvider(id: string) {
    setSavingProvider(true)
    try {
      const payload = providerDraftToPayload(providerDraft)
      await adminUpdateProvider(id, payload)
      setProviders(prev => prev.map(p => p.id === id ? { ...p, ...payload } : p))
      setEditingProviderId(null); setProviderDraft(emptyProvider()); invalidateFindHelpCache()
    } catch (err) { console.warn('[CC Resources] save provider failed:', err) }
    finally { setSavingProvider(false) }
  }

  async function deleteProvider(id: string) {
    setDeletingProvider(true)
    try {
      await adminDeleteProvider(id)
      setProviders(prev => prev.filter(p => p.id !== id))
      setDeleteProviderConfirmId(null); invalidateFindHelpCache()
    } catch (err) { console.warn('[CC Resources] delete provider failed:', err) }
    finally { setDeletingProvider(false) }
  }

  // Gov CRUD
  async function addGov() {
    setSavingGov(true)
    try {
      const payload = { ...govDraft, tags: govDraft.tags.split(',').map(s => s.trim()).filter(Boolean) }
      const id = await adminCreateGovProgram(payload)
      setGovPrograms(prev => [...prev, { id, ...payload, createdAt: Date.now(), updatedAt: Date.now() }].sort((a, b) => a.name.localeCompare(b.name)))
      setGovDraft(emptyGov()); setAddingGov(false); invalidateFindHelpCache()
    } catch (err) { console.warn('[CC Resources] add gov failed:', err) }
    finally { setSavingGov(false) }
  }

  async function saveGov(id: string) {
    setSavingGov(true)
    try {
      const payload = { ...govDraft, tags: govDraft.tags.split(',').map(s => s.trim()).filter(Boolean) }
      await adminUpdateGovProgram(id, payload)
      setGovPrograms(prev => prev.map(p => p.id === id ? { ...p, ...payload } : p))
      setEditingGovId(null); setGovDraft(emptyGov()); invalidateFindHelpCache()
    } catch (err) { console.warn('[CC Resources] save gov failed:', err) }
    finally { setSavingGov(false) }
  }

  async function deleteGov(id: string) {
    setDeletingGov(true)
    try {
      await adminDeleteGovProgram(id)
      setGovPrograms(prev => prev.filter(p => p.id !== id))
      setDeleteGovConfirmId(null); invalidateFindHelpCache()
    } catch (err) { console.warn('[CC Resources] delete gov failed:', err) }
    finally { setDeletingGov(false) }
  }

  // NGO CRUD
  async function addNgo() {
    setSavingNgo(true)
    try {
      const payload = { ...ngoDraft, tags: ngoDraft.tags.split(',').map(s => s.trim()).filter(Boolean) }
      const id = await adminCreateNgoProgram(payload)
      setNgoPrograms(prev => [...prev, { id, ...payload, createdAt: Date.now(), updatedAt: Date.now() }].sort((a, b) => a.name.localeCompare(b.name)))
      setNgoDraft(emptyNgo()); setAddingNgo(false); invalidateFindHelpCache()
    } catch (err) { console.warn('[CC Resources] add ngo failed:', err) }
    finally { setSavingNgo(false) }
  }

  async function saveNgo(id: string) {
    setSavingNgo(true)
    try {
      const payload = { ...ngoDraft, tags: ngoDraft.tags.split(',').map(s => s.trim()).filter(Boolean) }
      await adminUpdateNgoProgram(id, payload)
      setNgoPrograms(prev => prev.map(p => p.id === id ? { ...p, ...payload } : p))
      setEditingNgoId(null); setNgoDraft(emptyNgo()); invalidateFindHelpCache()
    } catch (err) { console.warn('[CC Resources] save ngo failed:', err) }
    finally { setSavingNgo(false) }
  }

  async function deleteNgo(id: string) {
    setDeletingNgo(true)
    try {
      await adminDeleteNgoProgram(id)
      setNgoPrograms(prev => prev.filter(p => p.id !== id))
      setDeleteNgoConfirmId(null); invalidateFindHelpCache()
    } catch (err) { console.warn('[CC Resources] delete ngo failed:', err) }
    finally { setDeletingNgo(false) }
  }

  const pendingCount = resourceSubmissions.filter(s => s.status === 'pending').length
  const filteredSubmissions = resourceFilter === 'all' ? resourceSubmissions : resourceSubmissions.filter(s => s.status === resourceFilter)
  const submissionCounts = {
    all: resourceSubmissions.length,
    pending: pendingCount,
    approved: resourceSubmissions.filter(s => s.status === 'approved').length,
    rejected: resourceSubmissions.filter(s => s.status === 'rejected').length,
  }

  if (!canUseCommandCenter) {
    return (
      <div className="px-5 py-8 max-w-xl mx-auto">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm">
          <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
            <i className="fa-solid fa-lock text-xl text-slate-400" />
          </div>
          <h1 className="font-display font-bold text-lg text-slate-900">Access restricted</h1>
          <p className="text-sm text-slate-500 mt-2 leading-relaxed">Resource management is limited to authorized platform administrators.</p>
          <button onClick={() => navigate(ROUTES.COMMAND_CENTER)} className="mt-5 rounded-lg bg-beacon-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-beacon-700 transition-colors">Back to Command Center</button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="bg-beacon-600 h-16 px-4 flex items-center gap-3 lg:border-l border-beacon-700">
        <button onClick={() => navigate(ROUTES.COMMAND_CENTER)} className="w-8 h-8 rounded-xl bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors flex-shrink-0">
          <i className="fa-solid fa-chevron-left text-white text-xs" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="font-display font-bold text-lg text-white">Resources</h1>
          <p className="text-[10px] text-white/70">Manage jobs, care providers, aid programs, and community submissions</p>
        </div>
      </div>

      <div className="px-4 pt-4 max-w-5xl mx-auto">
        <div className="bg-slate-200 rounded-xl p-1 flex gap-1 mb-4">
          {([
            { id: 'submissions', label: `Submissions${pendingCount > 0 ? ` · ${pendingCount}` : ''}` },
            { id: 'jobs', label: 'Jobs' },
            { id: 'care', label: 'Care' },
            { id: 'aid', label: 'Aid' },
          ] as { id: MainTab; label: string }[]).map(tab => (
            <button
              key={tab.id}
              onClick={() => setMainTab(tab.id)}
              className={`flex-1 rounded-[9px] px-2.5 py-1.5 text-[12px] font-semibold whitespace-nowrap transition-all ${mainTab === tab.id ? 'bg-white text-slate-900 shadow-[0_1px_3px_rgba(0,0,0,0.12)]' : 'text-slate-600 hover:text-slate-800'}`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="px-4 pb-8 space-y-3 max-w-5xl mx-auto">

        {/* ── Submissions ── */}
        {mainTab === 'submissions' && (
          <>
            <div className="bg-slate-200 rounded-xl p-1 flex gap-1 overflow-x-auto scrollbar-none">
              {([
                { id: 'pending', label: `Pending ${submissionCounts.pending}` },
                { id: 'approved', label: `Approved ${submissionCounts.approved}` },
                { id: 'rejected', label: `Rejected ${submissionCounts.rejected}` },
                { id: 'all', label: `All ${submissionCounts.all}` },
              ] as { id: ResourceSubmissionStatus | 'all'; label: string }[]).map(f => (
                <button key={f.id} onClick={() => setResourceFilter(f.id)} className={`flex-1 min-w-fit rounded-[9px] px-2.5 py-1.5 text-[11px] font-semibold whitespace-nowrap transition-all ${resourceFilter === f.id ? 'bg-white text-slate-900 shadow-[0_1px_3px_rgba(0,0,0,0.12)]' : 'text-slate-600 hover:text-slate-800'}`}>{f.label}</button>
              ))}
            </div>

            {filteredSubmissions.length === 0 ? (
              <div className="bg-white rounded-xl border border-dashed border-slate-200 px-3 py-5 text-center">
                <i className="fa-solid fa-book-open text-2xl text-slate-300 mb-2 block" />
                <p className="text-sm text-slate-400">No {resourceFilter === 'all' ? '' : resourceFilter} submissions.</p>
              </div>
            ) : (
              <div className="space-y-1.5">
                {filteredSubmissions.map(s => {
                  const expanded = expandedSubmissionId === s.id
                  const createdAt = s.createdAt?.toDate?.()
                  const reviewedAt = s.reviewedAt?.toDate?.()
                  return (
                    <div key={s.id} className="bg-white rounded-xl border border-beacon-100 px-3 py-2.5 space-y-2">
                      <div className="flex items-start gap-2.5">
                        <div className="w-8 h-8 rounded-lg bg-beacon-50 flex items-center justify-center flex-shrink-0">
                          <i className="fa-solid fa-lightbulb text-beacon-500 text-xs" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                            <p className="text-sm font-semibold text-slate-900 leading-tight">{s.title}</p>
                            <span className="text-[10px] font-semibold text-beacon-700 bg-beacon-50 border border-beacon-100 rounded-full px-2 py-0.5 uppercase">{s.type}</span>
                            <span className={`text-[10px] font-semibold rounded-full px-2 py-0.5 uppercase border ${s.status === 'approved' ? 'bg-success-50 text-success-700 border-success-100' : s.status === 'rejected' ? 'bg-danger-50 text-danger-700 border-danger-100' : 'bg-warning-50 text-warning-700 border-warning-100'}`}>{s.status}</span>
                          </div>
                          <p className={`text-xs text-slate-500 leading-relaxed ${expanded ? '' : 'line-clamp-1'}`}>{s.description}</p>
                          <p className="text-[10px] text-slate-400 mt-1">By {s.submitterName || s.submitterEmail || 'Unknown'} · {s.submitterRole}{createdAt && <> · {createdAt.toLocaleDateString()}</>}</p>
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <button onClick={() => startEditSubmission(s)} className="w-7 h-7 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"><i className="fa-solid fa-pen text-[10px]" /></button>
                          <button onClick={() => setDeleteSubmissionConfirmId(deleteSubmissionConfirmId === s.id ? null : s.id)} className="w-7 h-7 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-400 hover:text-danger-600 hover:bg-danger-50 hover:border-danger-200 transition-colors"><i className="fa-solid fa-trash text-[10px]" /></button>
                          <button onClick={() => setExpandedSubmissionId(expanded ? null : s.id)} className="w-7 h-7 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"><i className={`fa-solid fa-chevron-down text-xs transition-transform ${expanded ? 'rotate-180' : ''}`} /></button>
                        </div>
                      </div>
                      {deleteSubmissionConfirmId === s.id && (
                        <div className="flex items-center gap-2 rounded-lg bg-danger-50 border border-danger-200 px-3 py-2">
                          <p className="text-xs font-semibold text-danger-700 flex-1">Delete permanently?</p>
                          <button onClick={() => setDeleteSubmissionConfirmId(null)} className="text-xs font-semibold text-slate-500 px-2 py-1 rounded-lg hover:bg-white">Cancel</button>
                          <button onClick={() => deleteSubmission(s.id)} disabled={deletingSubmissionId === s.id} className="text-xs font-semibold text-white bg-danger-500 hover:bg-danger-600 disabled:opacity-50 px-3 py-1 rounded-lg">{deletingSubmissionId === s.id ? '…' : 'Delete'}</button>
                        </div>
                      )}
                      {expanded && editingSubmissionId === s.id ? (
                        <div className="rounded-xl bg-slate-50 border border-slate-200 p-2.5 space-y-2">
                          <input value={editFields.title} onChange={e => setEditFields(f => ({ ...f, title: e.target.value }))} placeholder="Title" className={inputCls} />
                          {CATEGORY_OPTIONS[s.type].length > 0 ? (
                            <select value={editFields.category} onChange={e => setEditFields(f => ({ ...f, category: e.target.value }))} className={`${inputCls} text-slate-700`}>
                              <option value="">— Category (optional) —</option>
                              {CATEGORY_OPTIONS[s.type].map(opt => <option key={opt} value={opt}>{opt}</option>)}
                            </select>
                          ) : (
                            <input value={editFields.category} onChange={e => setEditFields(f => ({ ...f, category: e.target.value }))} placeholder="Category (optional)" className={inputCls} />
                          )}
                          <input value={editFields.link} onChange={e => setEditFields(f => ({ ...f, link: e.target.value }))} placeholder="Link or contact (optional)" className={inputCls} />
                          <textarea value={editFields.description} onChange={e => setEditFields(f => ({ ...f, description: e.target.value }))} rows={3} placeholder="Description" className={`${inputCls} resize-none`} />
                          <textarea value={editFields.reviewNote} onChange={e => setEditFields(f => ({ ...f, reviewNote: e.target.value }))} rows={2} placeholder="Review note (optional)" className={`${inputCls} resize-none`} />
                          <div className="flex gap-2">
                            <button onClick={() => setEditingSubmissionId(null)} className="flex-1 py-1.5 rounded-xl border border-slate-200 text-xs font-semibold text-slate-500 hover:bg-slate-50">Cancel</button>
                            <button onClick={() => saveSubmission(s.id)} disabled={!editFields.title.trim() || !editFields.description.trim() || savingSubmission} className="flex-1 py-1.5 rounded-lg bg-beacon-600 text-xs font-semibold text-white hover:bg-beacon-700 disabled:opacity-50">{savingSubmission ? 'Saving…' : 'Save'}</button>
                          </div>
                        </div>
                      ) : expanded && (
                        <div className="rounded-xl bg-slate-50 border border-slate-200 p-2.5 space-y-2.5">
                          <div className="grid gap-1.5 text-xs text-slate-600 sm:grid-cols-2">
                            <p><span className="font-semibold text-slate-800">Submitter:</span> {s.submitterName || 'Unknown'}</p>
                            <p><span className="font-semibold text-slate-800">Email:</span> {s.submitterEmail || 'Not provided'}</p>
                            <p><span className="font-semibold text-slate-800">Category:</span> {s.category || 'Not set'}</p>
                            <p><span className="font-semibold text-slate-800">Submitted:</span> {createdAt ? createdAt.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : 'Unknown'}</p>
                            {s.link && <p className="sm:col-span-2"><span className="font-semibold text-slate-800">Link:</span> <a href={s.link} target="_blank" rel="noreferrer" className="text-beacon-600 hover:text-beacon-700 break-all">{s.link}</a></p>}
                            {reviewedAt && <p className="sm:col-span-2"><span className="font-semibold text-slate-800">Reviewed:</span> {reviewedAt.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}{s.reviewedBy ? ` by ${s.reviewedBy}` : ''}</p>}
                          </div>
                          {s.status === 'pending' ? (
                            <>
                              <textarea value={reviewNotes[s.id] ?? ''} onChange={e => setReviewNotes(prev => ({ ...prev, [s.id]: e.target.value }))} placeholder="Optional review note..." rows={2} className={`${inputCls} resize-none`} />
                              <div className="flex gap-2">
                                <button onClick={() => reviewSubmission(s.id, 'rejected')} disabled={reviewingSubmissionId === s.id} className="flex-1 py-1.5 rounded-xl border border-slate-200 text-xs font-semibold text-slate-500 hover:bg-slate-50 disabled:opacity-50">Reject</button>
                                <button onClick={() => reviewSubmission(s.id, 'approved')} disabled={reviewingSubmissionId === s.id} className="flex-1 py-1.5 rounded-lg bg-beacon-600 text-xs font-semibold text-white hover:bg-beacon-700 disabled:opacity-50">Approve</button>
                              </div>
                            </>
                          ) : (
                            <div className="rounded-xl bg-white border border-slate-200 px-3 py-2">
                              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Review note</p>
                              <p className="text-sm text-slate-700 mt-1">{s.reviewNote || 'No note added.'}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </>
        )}

        {/* ── Jobs ── */}
        {mainTab === 'jobs' && (
          <ListSection
            label="Jobs"
            items={jobs}
            loading={jobsLoading}
            renderItem={(job) => (
              <div>
                <p className="text-sm font-semibold text-slate-900 leading-tight">{job.title}</p>
                <p className="text-xs text-slate-500">{job.company}{job.location ? ` · ${job.location}` : ''}{job.pay ? ` · ${job.pay}` : ''}</p>
                <div className="flex gap-1.5 mt-1 flex-wrap">
                  {job.ra7277Compliant && <span className="text-[10px] font-semibold bg-settings-50 text-settings-700 rounded-full px-2 py-0.5">⚖️ RA 7277</span>}
                  <span className={`text-[10px] font-semibold rounded-full px-2 py-0.5 ${job.status === 'active' ? 'bg-success-50 text-success-700' : 'bg-slate-100 text-slate-500'}`}>{job.status}</span>
                </div>
              </div>
            )}
            form={<JobForm draft={jobDraft} onChange={(d) => { setJobDraft(d); if (editingJobId) { /* live update on edit */ } }} />}
            formValid={!!jobDraft.title.trim() && !!jobDraft.company.trim() && !!jobDraft.description.trim()}
            onAdd={addJob}
            onSave={saveJob}
            onDelete={deleteJob}
            editingId={editingJobId}
            setEditingId={(id) => { setEditingJobId(id); if (id) { const j = jobs.find(j => j.id === id); if (j) setJobDraft(jobToEdit(j)) } else setJobDraft(emptyJob()) }}
            addingNew={addingJob}
            setAddingNew={(v) => { setAddingJob(v); if (v) setJobDraft(emptyJob()) }}
            saving={savingJob}
            deleteConfirmId={deleteJobConfirmId}
            setDeleteConfirmId={setDeleteJobConfirmId}
            deleting={deletingJob}
          />
        )}

        {/* ── Care ── */}
        {mainTab === 'care' && (
          <ListSection
            label="Providers"
            items={providers}
            loading={providersLoading}
            renderItem={(p) => (
              <div>
                <p className="text-sm font-semibold text-slate-900 leading-tight">{p.name}</p>
                <p className="text-xs text-slate-500">{p.category} · {p.address}</p>
                <div className="flex gap-1.5 mt-1 flex-wrap">
                  {p.ra7277Compliant && <span className="text-[10px] font-semibold bg-settings-50 text-settings-700 rounded-full px-2 py-0.5">⚖️ RA 7277</span>}
                  {p.pwdDiscount && <span className="text-[10px] font-semibold bg-beacon-50 text-beacon-700 rounded-full px-2 py-0.5">💙 PWD Discount</span>}
                  {p.beaconQrEnabled && <span className="text-[10px] font-semibold bg-community-50 text-community-700 rounded-full px-2 py-0.5">⚡ Beacon QR</span>}
                  <span className={`text-[10px] font-semibold rounded-full px-2 py-0.5 ${p.status === 'active' ? 'bg-success-50 text-success-700' : 'bg-slate-100 text-slate-500'}`}>{p.status}</span>
                </div>
              </div>
            )}
            form={<ProviderForm draft={providerDraft} onChange={(d) => setProviderDraft(d)} />}
            formValid={!!providerDraft.name.trim() && !!providerDraft.category && !!providerDraft.address.trim() && !!providerDraft.description.trim()}
            onAdd={addProvider}
            onSave={saveProvider}
            onDelete={deleteProvider}
            editingId={editingProviderId}
            setEditingId={(id) => { setEditingProviderId(id); if (id) { const p = providers.find(p => p.id === id); if (p) setProviderDraft(providerToEdit(p)) } else setProviderDraft(emptyProvider()) }}
            addingNew={addingProvider}
            setAddingNew={(v) => { setAddingProvider(v); if (v) setProviderDraft(emptyProvider()) }}
            saving={savingProvider}
            deleteConfirmId={deleteProviderConfirmId}
            setDeleteConfirmId={setDeleteProviderConfirmId}
            deleting={deletingProvider}
          />
        )}

        {/* ── Aid ── */}
        {mainTab === 'aid' && (
          <>
            <div className="bg-slate-200 rounded-xl p-1 flex gap-1">
              <button onClick={() => setAidSubTab('gov')} className={`flex-1 rounded-[9px] px-3 py-1.5 text-[12px] font-semibold transition-all ${aidSubTab === 'gov' ? 'bg-white text-slate-900 shadow-[0_1px_3px_rgba(0,0,0,0.12)]' : 'text-slate-600 hover:text-slate-800'}`}>🏛️ Gov Aid ({govPrograms.length})</button>
              <button onClick={() => setAidSubTab('ngo')} className={`flex-1 rounded-[9px] px-3 py-1.5 text-[12px] font-semibold transition-all ${aidSubTab === 'ngo' ? 'bg-white text-slate-900 shadow-[0_1px_3px_rgba(0,0,0,0.12)]' : 'text-slate-600 hover:text-slate-800'}`}>🌿 NGO/CSR ({ngoPrograms.length})</button>
            </div>

            {aidSubTab === 'gov' && (
              <ListSection
                label="Gov Programs"
                items={govPrograms}
                loading={aidLoading}
                renderItem={(p) => (
                  <div>
                    <p className="text-sm font-semibold text-slate-900 leading-tight">{p.icon} {p.name}</p>
                    <p className="text-xs text-slate-500">{p.amount}{p.region ? ` · ${p.region}` : ''}</p>
                  </div>
                )}
                form={<GovForm draft={govDraft} onChange={setGovDraft} />}
                formValid={!!govDraft.name.trim() && !!govDraft.description.trim()}
                onAdd={addGov}
                onSave={saveGov}
                onDelete={deleteGov}
                editingId={editingGovId}
                setEditingId={(id) => { setEditingGovId(id); if (id) { const p = govPrograms.find(p => p.id === id); if (p) setGovDraft(govToEdit(p)) } else setGovDraft(emptyGov()) }}
                addingNew={addingGov}
                setAddingNew={(v) => { setAddingGov(v); if (v) setGovDraft(emptyGov()) }}
                saving={savingGov}
                deleteConfirmId={deleteGovConfirmId}
                setDeleteConfirmId={setDeleteGovConfirmId}
                deleting={deletingGov}
              />
            )}

            {aidSubTab === 'ngo' && (
              <ListSection
                label="NGO Programs"
                items={ngoPrograms}
                loading={aidLoading}
                renderItem={(p) => (
                  <div>
                    <p className="text-sm font-semibold text-slate-900 leading-tight">{p.icon} {p.name}</p>
                    <p className="text-xs text-slate-500">{p.amount}{p.contact ? ` · ${p.contact}` : ''}</p>
                  </div>
                )}
                form={<NgoForm draft={ngoDraft} onChange={setNgoDraft} />}
                formValid={!!ngoDraft.name.trim() && !!ngoDraft.contact.trim() && !!ngoDraft.description.trim()}
                onAdd={addNgo}
                onSave={saveNgo}
                onDelete={deleteNgo}
                editingId={editingNgoId}
                setEditingId={(id) => { setEditingNgoId(id); if (id) { const p = ngoPrograms.find(p => p.id === id); if (p) setNgoDraft(ngoToEdit(p)) } else setNgoDraft(emptyNgo()) }}
                addingNew={addingNgo}
                setAddingNew={(v) => { setAddingNgo(v); if (v) setNgoDraft(emptyNgo()) }}
                saving={savingNgo}
                deleteConfirmId={deleteNgoConfirmId}
                setDeleteConfirmId={setDeleteNgoConfirmId}
                deleting={deletingNgo}
              />
            )}
          </>
        )}

      </div>
    </div>
  )
}
